import { useEffect, useState } from "react";
import Table from "../components/Table";
import Modal from "../components/Modal";
import FotoUpload from "../components/FotoUpload";
import Form from "../components/Form";
import TIPOS_EQUIPO from "../constants/equipos";
import ModalFechas from "../components/ModalFechas";
import ModalAsignar from "../components/ModalAsignar";
import ModalTicket from '../components/ModalTicket';

const API_URL = process.env.REACT_APP_API_URL;

const ESTADO_INICIAL = {
  id: null, tipo_equipo: 'Desktop', numero_inventario: null, fecha_compra: null,
  marca: '', modelo: '', numero_serie: '', procesador: '', ram: '', almacenamiento: '',
  sistema_operativo: '', pulgadas: '', resolucion: '', capacidad_watts: '', tipo_conexion: '',
  cuenta_m365_email: '', antivirus_instalado: true, estado: 'Disponible', observaciones: '',
  sucursal_id: '', fecha_mantenimiento: null, foto_file: null, foto_preview: null
};

export default function EquiposScreen({ currentUser, token }) {
  const isAdmin = currentUser?.rol === 'admin';
  const isGerente = currentUser?.rol === 'gerente';
  const [equipos, setEquipos] = useState([]);
  const [sucursales, setSucursales] = useState([]);
  const [usuarios, setUsuarios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [showBajas, setShowBajas] = useState(false);
  const [equipoForm, setEquipoForm] = useState(ESTADO_INICIAL);
  const [equipoAsignar, setEquipoAsignar] = useState(null);
  const [equipoTicket, setEquipoTicket] = useState(null);
  const [usuarioId, setUsuarioId] = useState('');
  const [equipoFechas, setEquipoFechas] = useState(null);
  const [fechas, setFechas] = useState(null);
  const [modal, setModal] = useState({ visible: false, titulo: '', mensaje: '', tipo: 'info', onConfirm: null });
  const showModal = (tipo, titulo, mensaje, onConfirm = null) =>
    setModal({ visible: true, tipo, titulo, mensaje, onConfirm });
  const set = (campo) => (v) => setEquipoForm(f => ({ ...f, [campo]: v }));
  const fotoUrl = (url) => url ? `${API_URL.replace('/api', '')}${url}` : null;


  // ── Carga ─────────────────────────────────────────────────────────────
  const cargar = async () => {
    setLoading(true);
    try {
      const [resE, resS, resU] = await Promise.all([
        fetch(`${API_URL}/equipos`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API_URL}/sucursales`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API_URL}/usuarios`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      setUsuarios(await resU.json());
      setEquipos(await resE.json());
      setSucursales(await resS.json());
    } catch {
      showModal("error", "Error", "No se pudieron cargar los datos");
    } finally {
      setLoading(false);
    }
  };

  const cargarBajas = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/equipos/bajas`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEquipos(await res.json());
    } catch {
      showModal("error", "Error", "No se pudieron cargar las bajas");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { cargar(); }, []);

  // ── Handlers ──────────────────────────────────────────────────────────
  const abrirEditar = (row) => {
    setEquipoForm({ ...ESTADO_INICIAL, ...row, foto_file: null, foto_preview: fotoUrl(row.foto_url), fecha_compra: row.fecha_compra ? row.fecha_compra.split('T')[0] : null, fecha_mantenimiento: row.fecha_mantenimiento ? row.fecha_mantenimiento.split('T')[0] : null });
    setShowForm(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const abrirNuevo = () => {
    setEquipoForm(ESTADO_INICIAL);
    setShowForm(true);
  };
  const cerrarForm = () => {
    setShowForm(false);
    setEquipoForm(ESTADO_INICIAL);
  };

  const abrirFechas = async (row) => {
    try {
      const res = await fetch(`${API_URL}/equipos/fechas/${row.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      setFechas(data);
      setEquipoFechas(row);
    } catch {
      showModal('error', 'Error', 'No se pudieron cargar las fechas');
    }
  };

  const cerrarFechas = () => {
    setEquipoFechas(null);
    setFechas(null);
  };

  const abrirAsignar = (row) => {
    setEquipoAsignar(row);
    setUsuarioId('');
  };
  const cerrarAsignar = () => {
    setEquipoAsignar(null);
    setUsuarioId('');
  };

  const handleSubirFoto = async (id, archivo) => {
    const formData = new FormData();
    formData.append('foto', archivo);
    await fetch(`${API_URL}/equipos/${id}/foto`, {
      method: 'PUT',
      headers: { Authorization: `Bearer ${token}` },
      body: formData,
    });
  };

  const handleGuardar = async (e) => {
    e.preventDefault();
    try {
      const { foto_file, foto_preview, ...resto } = equipoForm;
      const esEdicion = !!equipoForm.id;
      const res = await fetch(
        esEdicion ? `${API_URL}/equipos/${equipoForm.id}` : `${API_URL}/equipos`,
        {
          method: esEdicion ? 'PUT' : 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify(resto)
        }
      );
      const data = await res.json();
      if (!res.ok) throw new Error(data.message);

      if (foto_file) await handleSubirFoto(data.id ?? equipoForm.id, foto_file);

      cerrarForm();
      cargar();
      showModal('success', 'Listo', esEdicion ? 'Equipo actualizado' : 'Equipo creado');
    } catch (err) {
      showModal('error', 'Error', err.message);
    }
  };

  const handleParsearBelarc = async (e) => {
    const archivo = e.target.files[0];
    if (!archivo) return;
    const formData = new FormData();
    formData.append('file', archivo);
    try {
      const res = await fetch(`${API_URL}/equipos/parse-belarc`, {
        method: 'POST', body: formData, headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      setEquipoForm(f => ({ ...f, ...data }));
    } catch {
      showModal('error', 'Error', 'No se pudo leer el archivo');
    }
  };

  const handleAsignar = async (e) => {
    e.preventDefault();
    try {
      const res = await fetch(`${API_URL}/asignaciones/asignar`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ usuario_id: usuarioId, equipo_id: equipoAsignar.id })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.message);
      cerrarAsignar();
      cargar();
      showModal('success', 'Listo', 'Equipo asignado correctamente');
    } catch (err) {
      showModal('error', 'Error', err.message);
    }
  };

  const handleDesasignar = async (row) => {
    showModal('confirm', '¿Desasignar equipo?',
      `Se desasignará ${row.marca} ${row.modelo}`,
      async () => {
        try {
          const resU = await fetch(`${API_URL}/asignaciones/${row.id}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          const { usuario_id } = await resU.json();
          const res = await fetch(`${API_URL}/asignaciones/desasignar`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
            body: JSON.stringify({ equipo_id: row.id, usuario_id })
          });
          const data = await res.json().catch(() => ({}));
          if (!res.ok) throw new Error(data.message);
          cargar();
          showModal('success', 'Listo', 'Equipo desasignado correctamente');
        } catch (err) {
          showModal('error', 'Error', err.message);
        }
      }
    );
  };
  // ── Config tabla ──────────────────────────────────────────────────────
  const columnas = [
    {
      header: "Foto", render: (row) => (
        <FotoUpload foto={fotoUrl(row.foto_url)} nombre={row.marca}
          onUpload={(archivo) => handleSubirFoto(row.id, archivo).then(cargar)} />
      )
    },
    { header: "N° Inventario", accessor: "numero_inventario" },
    { header: "Marca", accessor: "marca" },
    { header: "Modelo", accessor: "modelo" },
    { header: "N° Serie", accessor: "numero_serie" },
    { header: "Tipo", accessor: "tipo_equipo" },
    { header: "Estado", accessor: "estado" },
    {
      header: "Próximo Mantenimiento", render: (row) => {
        if (!row.fecha_mantenimiento) return "Sin fecha";
        const hoy = new Date();
        const fecha = new Date(row.fecha_mantenimiento);
        const diffTime = fecha - hoy;
        const diffDias = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        let color = "";
        let letra = "white"
        if (diffDias > 30) {
          color = "#00928F"; // verde
        } else if (diffDias > 7) {
          color = "#f2d069";
          letra = "black";
        } else {
          color = "#b72020";
        }
        const texto = fecha.toLocaleDateString('es-MX', {
          day: 'numeric',
          month: 'long',
          year: 'numeric',
          timeZone: 'UTC'
        });
        return (<span style={{ backgroundColor: color, color: letra, padding: "4px 8px", borderRadius: "5px", fontWeight: "bold" }}>{texto}</span>);
      }
    },
    ...((isAdmin || isGerente) ? [{
      header: "Acciones",
      render: (row) => (
        <>
          {/* Solo admin */}
          {isAdmin && (
            <>
              <button
                className="btn-edit"
                onClick={() => abrirEditar(row)}
              >
                Editar
              </button>

              {row.estado === 'Disponible' && (
                <button
                  className="btn-edit"
                  style={{ background: '#28a745', color: 'white' }}
                  onClick={() => abrirAsignar(row)}
                >
                  Asignar
                </button>
              )}

              {row.estado === 'Asignado' && (
                <button
                  className="btn-delete"
                  onClick={() => handleDesasignar(row)}
                >
                  Desasignar
                </button>
              )}
            </>
          )}

          {/* Admin y gerente */}
          {(isAdmin || isGerente) && (
            <>
              <button className="btn-edit" onClick={() => abrirFechas(row)}>
                Fechas
              </button>
              <button className="btn-edit" style={{ background: '#6f42c1', color: 'white' }} onClick={() => setEquipoTicket(row)}>
                Ticket
              </button>
            </>
          )}
        </>
      )
    }] : []),
  ];
  // ── Config form ───────────────────────────────────────────────────────
  const campos = [
    {
      fullRow: true, render: () => (
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
          <FotoUpload
            foto={equipoForm.foto_preview}
            nombre={equipoForm.marca}
            editable={true}          // ← siempre editable en el form
            onUpload={(archivo) => setEquipoForm(f => ({
              ...f,
              foto_file: archivo,
              foto_preview: URL.createObjectURL(archivo)
            }))}
          />
          <div>
            <label style={{ fontSize: 12, color: '#666' }}>Autocompletar desde Belarc (.html)</label>
            <input type="file" accept=".html,.htm" onChange={handleParsearBelarc} style={{ display: 'block', marginTop: 4 }} />
          </div>
        </div>
      )
    },
    { label: 'Tipo de equipo', type: 'select', value: equipoForm.tipo_equipo, onChange: set('tipo_equipo'), required: true, options: TIPOS_EQUIPO },
    { label: 'Sucursal', type: 'select', value: equipoForm.sucursal_id, onChange: set('sucursal_id'), required: true, options: [{ value: '', label: '-- Selecciona --' }, ...sucursales.map(s => ({ value: s.id, label: s.nombre }))] },
    { label: 'Marca', value: equipoForm.marca, onChange: set('marca'), required: true, placeholder: 'Marca' },
    { label: 'Modelo', value: equipoForm.modelo, onChange: set('modelo'), required: true, placeholder: 'Modelo' },
    { label: 'N° Serie', value: equipoForm.numero_serie, onChange: set('numero_serie'), required: true, placeholder: 'N° Serie' },
    { label: 'Estado', type: 'select', value: equipoForm.estado, onChange: set('estado'), options: ['Disponible', 'Asignado', 'En reparación', 'Desuso', 'Retirado'] },
    { label: 'N° Inventario', value: equipoForm.numero_inventario, onChange: set('numero_inventario'), placeholder: 'N° Inventario (Opcional)' },
    { label: 'Fecha de compra', type: 'date', value: equipoForm.fecha_compra, onChange: set('fecha_compra') },
    { label: 'Fecha de mantenimiento', type: 'date', value: equipoForm.fecha_mantenimiento, onChange: set('fecha_mantenimiento') },
    { label: 'Procesador', value: equipoForm.procesador, onChange: set('procesador'), placeholder: 'Procesador (Opcional)' },
    { label: 'RAM', value: equipoForm.ram, onChange: set('ram'), placeholder: 'RAM (Opcional)' },
    { label: 'Almacenamiento', value: equipoForm.almacenamiento, onChange: set('almacenamiento'), placeholder: 'Almacenamiento (Opcional)' },
    { label: 'Sistema Operativo', value: equipoForm.sistema_operativo, onChange: set('sistema_operativo'), placeholder: 'SO (Opcional)' },
    { label: 'Pulgadas', value: equipoForm.pulgadas, onChange: set('pulgadas'), placeholder: 'Pulgadas (Opcional)' },
    { label: 'Resolución', value: equipoForm.resolucion, onChange: set('resolucion'), placeholder: 'Resolución (Opcional)' },
    { label: 'Capacidad Watts', value: equipoForm.capacidad_watts, onChange: set('capacidad_watts'), placeholder: 'Watts (Opcional)' },
    { label: 'Tipo Conexión', value: equipoForm.tipo_conexion, onChange: set('tipo_conexion'), placeholder: 'Tipo conexión (Opcional)' },
    { label: 'Cuenta M365', value: equipoForm.cuenta_m365_email, onChange: set('cuenta_m365_email'), placeholder: 'Email M365 (Opcional)' },
    { label: 'Antivirus', type: 'select', value: String(equipoForm.antivirus_instalado), onChange: v => setEquipoForm(f => ({ ...f, antivirus_instalado: v === 'true' })), options: [{ value: 'true', label: 'Sí' }, { value: 'false', label: 'No' }] },
    { label: 'Observaciones', type: 'textarea', fullRow: true, value: equipoForm.observaciones, onChange: set('observaciones'), placeholder: 'Observaciones (Opcional)' },
  ];

  // ── Render ────────────────────────────────────────────────────────────
  if (loading) return <p>Cargando...</p>;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <h2>Equipos</h2>
        {isAdmin && (
          <button className="btn-secondary" onClick={() => {
            if (showBajas) { setShowBajas(false); cargar(); }
            else { setShowBajas(true); cargarBajas(); cerrarForm(); }
          }}>
            {showBajas ? 'Ver Equipos' : 'Ver Bajas'}
          </button>
        )}
        {!showBajas && isAdmin && (
          <button className="btn-secondary" onClick={showForm ? cerrarForm : abrirNuevo}>
            {showForm ? 'Cancelar' : 'Nuevo Equipo'}
          </button>
        )}
      </div>

      {showForm && (
        <Form campos={campos} onSubmit={handleGuardar} onCancel={cerrarForm}
          submitLabel={equipoForm.id ? 'Actualizar Equipo' : 'Crear Equipo'} />
      )}

      <Table columnas={columnas} datos={equipos} />
      <Modal modal={modal} setModal={setModal} />
      <ModalFechas equipo={equipoFechas} fechas={fechas} onClose={cerrarFechas} />
      <ModalAsignar item={equipoAsignar} usuarios={usuarios} usuarioId={usuarioId} onChangeUsuario={setUsuarioId} onSubmit={handleAsignar} onClose={cerrarAsignar} />
      <ModalTicket equipo={equipoTicket} usuario={currentUser} token={token} onClose={() => setEquipoTicket(null)}/>
    </div>
  );
}